"use strict";
/**
 * @fileoverview Module contains the function for building a Shader.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Shader = void 0;
const gl_1 = require("./gl");
const uniforms_1 = require("./uniforms");
const defaultOpts = {
    attributes: [],
    uniforms: [],
    mode: WebGLRenderingContext.TRIANGLE_STRIP,
    clear: true,
};
/**
 * Get the "clear" option value.
 */
const clearOption = (opts) => (opts.clear === undefined) || opts.clear;
/**
 * The default viewport is the size of the canvas we render to.
 */
const defaultViewport = (canvas) => [0, 0, canvas.width, canvas.height];
/**
 * Get the number of vertices a shader will render.
 */
const nVertices = (opts) => opts.indices ? opts.indices.length : opts.attributes[0].length();
const cache = new WeakMap();
/**
 * Look up a rendering context and WebGLProgram for a shader.
 * If they don't exist already, create one.
 */
const lookup = (key, canvas, vertexSrc, fragmentSrc) => {
    let item = cache.get(key);
    if (!item) {
        const gl = gl_1.glContext(canvas);
        item = { gl, program: gl_1.glProgram(gl, vertexSrc, fragmentSrc) };
        cache.set(key, item);
    }
    return item;
};
const pendingTextureRenders = new WeakMap();
/**
 * Prepare a texture uniform for rendering.
 * If the texture's content is the output of a shader, we actually
 * render the shader to the texture here.
 */
const prepareTextureUniforms = (gl, prog, canvas, uniforms) => {
    for (const uniform of uniforms) {
        if (!uniforms_1.isTextureUniform(uniform))
            continue;
        uniform.prepare(gl, prog);
        const renderFuncs = pendingTextureRenders.get(uniform);
        if (!renderFuncs)
            continue;
        pendingTextureRenders.delete(uniform);
        for (const func of renderFuncs) {
            func(canvas);
        }
    }
};
/**
 * Send the attributes and uniforms to a shader. If the shader
 * is using gl.drawElements(...) then also send the indices.
 */
const sendDataToShader = (gl, prog, opts) => {
    let firstLen;
    for (const attr of opts.attributes) {
        if (isNaN(firstLen)) {
            firstLen = attr.length();
        }
        else if (attr.length() != firstLen) {
            throw new Error('Mismatched attrbute size');
        }
        attr.send(gl, prog);
    }
    for (const uniform of opts.uniforms) {
        uniform.send(gl, prog);
    }
    if (opts.indices)
        gl_1.sendIndices(gl, opts.indices);
};
/**
 * Render a shader to either a canvas for a WebGLFramebuffer.
 */
const renderShader = (key, canvas, vertexSrc, fragmentSrc, frameBuffer, renderBuffer, opts) => {
    const { gl, program } = lookup(key, canvas, vertexSrc, fragmentSrc);
    prepareTextureUniforms(gl, program, canvas, opts.uniforms);
    gl.useProgram(program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
    gl.bindRenderbuffer(gl.RENDERBUFFER, renderBuffer);
    sendDataToShader(gl, program, opts);
    gl_1.glRender(gl, nVertices(opts), clearOption(opts), opts.viewport || defaultViewport(canvas), opts.mode || defaultOpts.mode, 
    /* drawElements= */ !!opts.indices);
};
/**
 * Render a shader to a cube texture. So far only cube cameras are supported.
 */
const renderCubeTexture = (key, canvas, opts, target, vertexSrc, fragmentSrc) => {
    if (target instanceof uniforms_1.CubeCameraUniformImpl) {
        const gl = gl_1.glContext(canvas);
        const viewport = opts.viewport || defaultViewport(canvas);
        const { frameBuffer, renderBuffer } = target.buffers(gl, viewport);
        target.render((cf) => {
            renderShader(key, canvas, vertexSrc, fragmentSrc, frameBuffer[cf], renderBuffer[cf], opts);
        });
        return;
    }
    // TODO render to cube texture without cube camera.
    throw new Error('Not implemented');
};
/**
 * Render a shader to a 2D texture.
 */
const render2DTexture = (key, canvas, opts, target, vertexSrc, fragmentSrc) => {
    const gl = gl_1.glContext(canvas);
    const viewport = opts.viewport || defaultViewport(canvas);
    const { frameBuffer, renderBuffer } = target.buffers(gl, viewport);
    renderShader(key, canvas, vertexSrc, fragmentSrc, frameBuffer, renderBuffer, opts);
};
/**
 * Create a function that delays rendering a shader to a texture
 * until a shader that uses the texture is rendered to a camera.
 */
const createTextureRenderFunc = (key, target, vertexSrc, fragmentSrc, opts) => (canvas) => {
    if (uniforms_1.isCubeTextureUniform(target)) {
        renderCubeTexture(key, canvas, opts, target, vertexSrc, fragmentSrc);
        return;
    }
    render2DTexture(key, canvas, opts, target, vertexSrc, fragmentSrc);
};
/**
 * Add a texture render function to the map.
 */
const addTextureRenderFunc = (target, func) => {
    let funcs = pendingTextureRenders.get(target);
    if (!funcs) {
        funcs = [];
        pendingTextureRenders.set(target, funcs);
    }
    funcs.push(func);
};
/**
 * Create a shader function to render to a target.
 * @param vertexSrc the vertex shader source
 * @param fragmentSrc the fragment shader source
 * @param options for the shader
 */
exports.Shader = (vertexSrc, fragmentSrc, options) => {
    var _a;
    if (typeof vertexSrc !== 'string') {
        throw new Error('The first argument of Shader must be a string');
    }
    if (typeof fragmentSrc !== 'string') {
        throw new Error('The second argument of Shader must be a string');
    }
    if (!(options && ((_a = options.attributes) === null || _a === void 0 ? void 0 : _a.length))) {
        throw new Error('Shader requires at least one attribute');
    }
    const key = {};
    return (target) => {
        if (target instanceof HTMLCanvasElement) {
            renderShader(key, target, vertexSrc, fragmentSrc, null, null, options);
            return target;
        }
        else if (uniforms_1.isTextureUniform(target)) {
            addTextureRenderFunc(target, createTextureRenderFunc(key, target, vertexSrc, fragmentSrc, options));
            return target;
        }
        throw new Error('Shader function must be called with a canvas or texture uniform');
    };
};
